// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * Transport session. Wraps trystero (nostr strategy) into typed actions for the
 * chat protocol. trystero discovers peers over public nostr relays and encrypts
 * the signalling handshake with the room passphrase, so a relay only sees
 * ciphertext. It builds a FULL MESH — every browser connects directly to every
 * other — so messages ride direct WebRTC data channels (DTLS on the wire) with
 * no server ever in the message path.
 *
 * Three actions:
 *   hello  — announce this participant's display name (JSON) — sent to a new peer
 *            on join, and re-broadcast on rename
 *   msg    — one encrypted message frame: [iv | ciphertext] (binary; trystero
 *            chunks larger image frames and reports progress)
 *   typing — a transient typing signal: { on } (JSON, broadcast)
 */
import { joinRoom, selfId } from 'trystero/nostr';
import type { Room, BaseRoomConfig, JsonValue, MessageAction } from '@trystero-p2p/core';

const APP_ID = 'chatwell-v1-benrichardson-dev';

export interface Hello {
  name: string;
}

export interface SessionHandlers {
  onPeerJoin: (peerId: string) => void;
  onPeerLeave: (peerId: string) => void;
  onHello: (peerId: string, hello: Hello) => void;
  onMessage: (peerId: string, frame: Uint8Array) => void;
  onTyping: (peerId: string, on: boolean) => void;
  onError: (message: string) => void;
}

export class ChatSession {
  readonly selfId = selfId;
  private room: Room | null = null;
  private helloA: MessageAction<JsonValue> | null = null;
  private msgA: MessageAction<Uint8Array> | null = null;
  private typingA: MessageAction<JsonValue> | null = null;

  constructor(
    private roomId: string,
    private pass: string,
    private handlers: SessionHandlers,
  ) {}

  /** Join the room and wire the actions. Returns false if joining threw. */
  join(): boolean {
    const config: BaseRoomConfig = { appId: APP_ID };
    if (this.pass) config.password = this.pass;
    let room: Room;
    try {
      room = joinRoom(config, this.roomId);
    } catch (e) {
      this.handlers.onError(`Could not join: ${(e as Error).message}`);
      return false;
    }
    this.room = room;

    this.helloA = room.makeAction<JsonValue>('hello');
    this.msgA = room.makeAction<Uint8Array>('msg');
    this.typingA = room.makeAction<JsonValue>('typing');

    this.helloA.onMessage = (data, ctx) => this.handlers.onHello(ctx.peerId, data as unknown as Hello);
    this.msgA.onMessage = (data, ctx) => this.handlers.onMessage(ctx.peerId, data as Uint8Array);
    this.typingA.onMessage = (data, ctx) => {
      const on = !!(data as unknown as { on?: boolean })?.on;
      this.handlers.onTyping(ctx.peerId, on);
    };

    room.onPeerJoin = (peerId) => this.handlers.onPeerJoin(peerId);
    room.onPeerLeave = (peerId) => this.handlers.onPeerLeave(peerId);
    return true;
  }

  /** Announce our name. Targeted to a specific peer, or broadcast to all. */
  sendHello(hello: Hello, target?: string): void {
    this.helloA?.send(hello as unknown as JsonValue, target ? { target } : undefined).catch(() => undefined);
  }

  /** Broadcast a transient typing signal to everyone. */
  sendTyping(on: boolean): void {
    this.typingA?.send({ on } as unknown as JsonValue).catch(() => undefined);
  }

  /**
   * Broadcast one encrypted message frame to every peer, reporting 0..1 upload
   * progress (for larger image frames). The message id rides as trystero
   * metadata so a receiver's progress can be attributed.
   */
  async broadcastMessage(frame: Uint8Array, id: string, onProgress?: (p: number) => void): Promise<void> {
    if (!this.msgA) throw new Error('not joined');
    await this.msgA.send(frame, {
      metadata: id,
      onProgress: onProgress ? (p) => onProgress(p) : undefined,
    });
  }

  getPeerIds(): string[] {
    return this.room ? Object.keys(this.room.getPeers()) : [];
  }

  leave(): void {
    if (!this.room) return;
    try {
      void this.room.leave();
    } catch {
      /* already left */
    }
    this.room = null;
  }
}
